import { Alert, AlertTitle, Button, LinearProgress, Snackbar } from '@mui/material';
import React from 'react';

export const UpdateNotification: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [installing, setInstalling] = React.useState(false);
  const [newVersion, setNewVersion] = React.useState<string | undefined>(undefined);
  const registrationRef = React.useRef<ServiceWorkerRegistration | null>(null);
  const waitingRef = React.useRef<ServiceWorker | null>(null);
  const controllerChangedRef = React.useRef(false);

  // registrace + detekce waiting
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.log('[SW-UI] service workers not supported');
      return;
    }

    if ((window as any).__swUIRegistered) return;
    (window as any).__swUIRegistered = true;

    let intervalId: number | undefined;

    const showIfWaiting = (reg: ServiceWorkerRegistration) => {
      if (reg.waiting && navigator.serviceWorker.controller) {
        waitingRef.current = reg.waiting;

        try {
          const ch = new MessageChannel();
          ch.port1.onmessage = (evt) => {
            const v = evt.data?.version;
            if (typeof v === 'string') setNewVersion(v);
          };
          reg.waiting.postMessage({ type: 'CHECK_VERSION' }, [ch.port2]);
        } catch {}
        setOpen(true);
      }
    };

    const register = async () => {
      try {
        // ověř, že sw.js existuje
        const probe = await fetch('/sw.js', { cache: 'no-store' });
        if (!probe.ok) console.warn('[SW-UI] /sw.js not OK, status', probe.status);

        const reg = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        registrationRef.current = reg;

        // hned po registraci – je něco waiting?
        showIfWaiting(reg);

        // updatefound → může přijít waiting
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              showIfWaiting(reg);
            }
          });
        });

        // periodicky kontroluj, ale jen když je tab viditelný
        const tick = async () => {
          if (document.visibilityState !== 'visible') return;
          try {
            await reg.update();
          } catch {}
          showIfWaiting(reg);
        };
        intervalId = window.setInterval(tick, 15000);
        document.addEventListener('visibilitychange', tick);

        // fallback po mountu
        setTimeout(() => showIfWaiting(reg), 3000);
      } catch (e) {
        console.warn('[SW-UI] registration failed:', e);
      }
    };

    register();

    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, []);

  const handleUpdate = () => {
    const reg = registrationRef.current;
    const waiting = waitingRef.current || reg?.waiting;
    if (!reg || !waiting) {
      console.log('[SW-UI] update clicked, but no waiting worker');
      return;
    }

    setInstalling(true);

    if (!controllerChangedRef.current) {
      controllerChangedRef.current = true;
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          console.log('[SW-UI] controllerchange detected. Forcing reload.');
          setTimeout(() => window.location.reload(), 50);
        },
        { once: true }
      );
    }

    try {
      waiting.postMessage({ type: 'SKIP_WAITING' });
      console.log('[SW-UI] SKIP_WAITING sent.');
    } catch {
      waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    console.log('[SW-UI] Fallback: Scheduling immediate reload.');
    setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  return (
    <Snackbar
      open={open}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={null}
    >
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={handleUpdate} disabled={installing}>
            {installing ? 'Instaluje…' : 'Aktualizovat'}
          </Button>
        }
      >
        <AlertTitle>Nová verze dostupná</AlertTitle>
        {installing && <LinearProgress sx={{ mb: 1 }} />}
        Je dostupná aktualizace aplikace{newVersion ? ` (${newVersion})` : ''}.
      </Alert>
    </Snackbar>
  );
};
